<template>
  <div class="w-full min-h-screen flex flex-col">
    <MainNav />
    <section class="w-full relative">
      <img 
        src="/image/83islands_location.jpg" 
        alt="83 Islands Distillery" 
        class="w-full h-[800px] object-cover object-[right_top] md:object-center"
      />
      
      <!-- Text Overlay -->
      <div class="mx-auto absolute mt-36 inset-0 z-20 w-full p-4 lg:p-36 flex flex-col items-center lg:items-start">
        <h1 class="text-white font-maax text-4xl uppercase lg:text-6xl font-bold text-center lg:text-left max-w-3xl leading-tight drop-shadow-[0_5px_5px_rgba(0,0,0,0.3)]">
          In the News
        </h1>
        <p class="text-white font-maax text-xl lg:text-2xl mt-4 text-center lg:text-left max-w-3xl drop-shadow-[0_5px_5px_rgba(0,0,0,0.3)]">
          Discover what the world is saying about 83 Islands Distillery
        </p>
      </div>

      <!-- Double Wave -->
      <div class="absolute -bottom-1 left-0 right-0 w-full overflow-hidden">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="w-full h-20 text-white">
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" 
                fill="currentColor" class="opacity-25"/>
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" 
                fill="currentColor"/>
        </svg>
      </div>
    </section>

    <main class="flex-grow w-full bg-white text-black">
      <div class="container mx-auto p-4">
        <div class="max-w-7xl mx-auto">
          
          <!-- Introduction -->
          <div class="text-center mb-16">
            <h2 class="text-4xl font-maax font-bold uppercase text-black pb-4">Media Coverage</h2>
            <p class="font-maax text-xl text-black max-w-4xl mx-auto">
              From international publications to local media, discover how 83 Islands Distillery is making waves in the world of premium rum and sustainable distilling.
            </p>
          </div>

          <!-- Search and Filter Controls -->
          <div class="mb-8">
            <div class="flex flex-col md:flex-row gap-4 mb-6">
              <!-- Search Bar -->
              <div class="flex-1">
                <div class="relative">
                  <input 
                    v-model="searchQuery"
                    @keyup.enter="handleSearch"
                    type="text" 
                    placeholder="Search articles..."
                    class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                  />
                  <button 
                    @click="handleSearch"
                    class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                  </button>
                </div>
              </div>
              
              <!-- Category Filter -->
              <div class="flex gap-2 flex-wrap">
                <button
                  v-for="category in newsCategories"
                  :key="category"
                  @click="handleCategoryChange(category)"
                  :class="[
                    'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                    selectedCategory === category
                      ? 'bg-gold text-black'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  ]"
                >
                  {{ category }}
                </button>
              </div>
            </div>
            
            <!-- Search Results Header -->
            <div v-if="showSearchResults" class="mb-4">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-semibold">
                  Search Results for "{{ searchQuery }}" ({{ filteredArticles.length }} articles)
                </h3>
                <button 
                  @click="clearSearch"
                  class="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Clear Search
                </button>
              </div>
            </div>
          </div>

          <!-- News Articles Grid -->
          <div v-if="filteredArticles.length > 0" class="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <article 
              v-for="article in filteredArticles" 
              :key="article.id"
              class="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <div class="relative w-full aspect-[4/3] bg-gray-200 overflow-hidden">
                <img 
                  :src="article.image" 
                  :alt="article.title"
                  class="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div class="p-6">
                <div class="flex items-center mb-2">
                  <span class="text-sm text-gray-500">{{ article.publication }}</span>
                  <span class="mx-2 text-gray-300">•</span>
                  <span class="text-sm text-gray-500">{{ article.date }}</span>
                </div>
                <h3 class="text-xl font-maax font-bold text-black mb-3 line-clamp-2">
                  {{ article.title }}
                </h3>
                <p class="text-gray-600 mb-4 line-clamp-3">
                  {{ article.excerpt }}
                </p>
                <div class="flex items-center justify-between">
                  <router-link 
                    v-if="article.isInternal"
                    :to="article.url" 
                    class="text-gold font-maax font-semibold hover:underline"
                  >
                    Read More →
                  </router-link>
                  <a 
                    v-else
                    :href="article.url" 
                    class="text-gold font-maax font-semibold hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Read More →
                  </a>
                  <span class="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                    {{ article.category }}
                  </span>
                </div>
              </div>
            </article>
          </div>
          
          <!-- No Results Message -->
          <div v-else class="text-center py-12">
            <div class="text-gray-500 text-lg mb-4">
              {{ showSearchResults ? 'No articles found matching your search.' : 'No articles found in this category.' }}
            </div>
            <button 
              v-if="showSearchResults"
              @click="clearSearch"
              class="text-gold font-maax font-semibold hover:underline"
            >
              View All Articles
            </button>
          </div>

          <!-- Call to Action -->
          <div class="bg-black text-white p-8 rounded-lg text-center">
            <h3 class="text-2xl font-maax font-bold mb-4">Want to Write About Us?</h3>
            <p class="text-lg mb-6">
              We welcome media inquiries and are happy to provide high-resolution images, press kits, and arrange interviews.
            </p>
            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact" 
                class="font-maax px-8 py-3 uppercase font-bold transition-all rounded-lg border-2 text-white hover:bg-white hover:text-black hover:border-white text-center"
              >
                Contact Us
              </a>
              <a 
                href="/tours" 
                class="font-maax px-8 py-3 uppercase font-bold transition-all rounded-lg border-2 text-white hover:bg-white hover:text-black hover:border-white text-center"
              >
                Book a Tour
              </a>
            </div>
          </div>

        </div>
      </div>
    </main>

    <Footer class="relative" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import MainNav from '../components/MainNav.vue'
import Footer from '../components/Footer.vue'
import { newsArticles, newsCategories, getArticlesByCategory, searchArticles } from '../data/newsArticles'

// Reactive data
const selectedCategory = ref('All')
const searchQuery = ref('')
const showSearchResults = ref(false)

// Computed properties
const filteredArticles = computed(() => {
  if (showSearchResults.value && searchQuery.value) {
    return searchArticles(searchQuery.value)
  }
  return getArticlesByCategory(selectedCategory.value)
})

// Methods
const handleCategoryChange = (category) => {
  selectedCategory.value = category
  showSearchResults.value = false
  searchQuery.value = ''
}

const handleSearch = () => {
  if (searchQuery.value.trim()) {
    showSearchResults.value = true
  } else {
    showSearchResults.value = false
  }
}

const clearSearch = () => {
  searchQuery.value = ''
  showSearchResults.value = false
}
</script>

<style>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.text-gold {
  color: #F6D993;
}
</style>
